import moment from 'moment'
import PropTypes from 'prop-types'
import { useRef } from 'react'
import { Popup } from '@progress/kendo-react-popup'
import { makeStyles, TextField } from '@material-ui/core'

import {
	DatePicker,
	TimePicker,
	DateTimePicker,
	MultiViewCalendar,
} from '@progress/kendo-react-dateinputs'

import { ConvertLocalTimeGet } from '~/utils'

import Styled from './input-datetime-kendo.styled'

const CustomPopup = (props) => {
	return <Popup {...props} style={{ zIndex: 5000, border: 0 }} />
}

const PickerComponent = (props) => {
	let cld = useRef(null)

	const CustomCalendar = (item) => {
		const min = new Date(props.minYear, props.minMonth - 1, props.minDay)
		const max = new Date(props.maxYear, props.maxMonth - 1, props.maxDay)
		const defaultValue = new Date(props.maxYear, props.maxMonth - 1, props.maxDay)
		return <MultiViewCalendar views={1} value={item.value}
			min={min}
			max={max} onChange={item.onChange}
			defaultValue={defaultValue} />
	}

	const limit = {}
	if (props.disableFuture) {
		limit.max = new Date(new Date().setHours(23, 59, 59, 999))
	}
	if (props.disablePast) {
		limit.min = new Date(new Date().setHours(0, 0, 0, 0))
	}

	const _formatDate = () => {
		return <Styled.FormatDate>{props.value}</Styled.FormatDate>
	}

	return (
		<>
			{props.smu && !props.time ? <DatePicker
				ref={cld}
				id={props.id}
				popup={CustomPopup}
				calendar={CustomCalendar}
				format='dd/MM/yyyy'
				formatPlaceholder={{ year: 'yyyy', month: 'mm', day: 'dd' }}
				width='100%'
				value={props.value ? new Date(props.value) : null}
				onChange={props.onChange}
				onBlur={props.onBlur}
				onFocus={() => {
					localStorage.setItem('calendar', cld.current ? cld.current.state.show : false)
				}}
				disabled={props.disabled}
				tabIndex={props.tabIndex}
				{...limit}
			/> : props.smu && props.time ?
				<TimePicker
					ref={cld}
					id={props.id}
					min={props.min ? props.min : null}
					popup={CustomPopup}
					format={props.justTime ? 'HH:mm' : 'dd/MM/yyyy HH:mm'}
					formatPlaceholder={props.justTime ? { hour: 'hh', minute: 'mm' } : { year: 'yyyy', month: 'mm', day: 'dd', hour: 'hh', minute: 'mm' }}
					width='100%'
					value={props.value ? new Date(props.value) : null}
					onChange={props.onChange}
					onBlur={props.onBlur}
					onFocus={() => {
						localStorage.setItem('calendar', cld.current ? cld.current.state.show : false)
					}}
					disabled={props.disabled}
					tabIndex={props.tabIndex}
					{...limit}
					dateInput={_formatDate}
				/> : <DateTimePicker
					ref={cld}
					id={props.id}
					popup={CustomPopup}
					format='dd/MM/yyyy HH:mm'
					formatPlaceholder={{ year: 'yyyy', month: 'mm', day: 'dd', hour: 'hh', minute: 'mm' }}
					width='100%'
					value={props.value ? new Date(props.value) : null}
					onChange={props.onChange}
					onBlur={props.onBlur}
					onFocus={() => {
						localStorage.setItem('calendar', cld.current ? cld.current.state.show : false)
					}}
					disabled={props.disabled}
					tabIndex={props.tabIndex}
					{...limit}
				/>}
		</>
	)
}

const useStyles = makeStyles(() => ({
	noBorder: {
		border: 'none',
	},
	style: {
		height: 44
	}
}))

const InputDateTimeKendo = (props) => {
	const classes = useStyles()

	const convertToLocal = (locationDesc, datetime) => {  // eslint-disable-line no-unused-vars
		if (locationDesc && datetime) {
			return ConvertLocalTimeGet(locationDesc, datetime, 'DD MMMM YYYY HH:mm')
		}
		return datetime
	}

	return (
		<>
			{props.isTitle && (
				<Styled.ContainerTitle>
					<Styled.Title>{props.title} {props.isRequired && (<Styled.RedStar>*</Styled.RedStar>)}</Styled.Title>
				</Styled.ContainerTitle>
			)}
			<Styled.Container>
				<Styled.Wrapper>
					<TextField
						smu={props.smu}
						time={props.time}
						fullWidth
						id='m_comp_input_datetime'
						variant='outlined'
						InputProps={{
							inputComponent: PickerComponent,
							inputProps: {
								disableFuture: props.disableFuture,
								disablePast: props.disablePast,
								tabIndex: props.tabIndex,
								id: props.id,
								disabled: !!props.readOnly,
								smu: props.smu,
								time: props.time,
								min: props.min,
								location: props.location,
								justTime: props.justTime,
								maxDay: props.maxDay,
								maxMonth: props.maxMonth,
								maxYear: props.maxYear,
								minDay: props.minDay,
								minMonth: props.minMonth,
								minYear: props.minYear,
							},
							className: props.className ? classes.style : null
						}}
						InputLabelProps={{
							shrink: true,
						}}
						error={props.form && props.form.touched[props.name] && !!props.form.errors[props.name]}
						helperText={
							props.form && props.name && props.form.touched[props.name] && props.form.errors[props.name]
								? <Styled.ErrorText>{props.form.errors[props.name]}</Styled.ErrorText>
								: ' '
						}
						value={
							props.form && convertToLocal(props.locationDesc, props.form.values[props.name]) ||
							convertToLocal(props.locationDesc, props.value) || null
						}
						onChange={
							props.onChange
								? ({ value: v }) => props.onChange(moment(v).toISOString())
								: ({ value: v }) => props.form.setFieldValue(props.name, moment(v).toISOString())
						}
						style={{ ...{ width: '100%' }, ...props.customstyle }}
						onBlur={props.onBlur ? (param) => props.onBlur(param) : null}
						{...props}
					/>
				</Styled.Wrapper>
			</Styled.Container>
		</>
	)
}

InputDateTimeKendo.defaultProps = {
	label: '',
	isTitle: false,
	customstyle: {},
}
InputDateTimeKendo.propTypes = {
	form: PropTypes.object.isRequired,
	name: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
}

export default InputDateTimeKendo
